// src/pages/organizations/OrganizationsPage.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Eye,
  Power,
  Loader2,
  Users,
  FolderOpen,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { organizationService } from '../../services/organization.service';
import {
  Organization,
  OrganizationStatus,
  OrganizationStats,
} from '../../types/organization.types';
import CreateOrganizationModal from '../../components/organizations/CreateOrganizationModal';
import toast from 'react-hot-toast';

const STATUS_CONFIG: Record<OrganizationStatus, { label: string; color: string }> = {
  active: { label: 'Active', color: 'bg-green-100 text-green-800' },
  inactive: { label: 'Inactive', color: 'bg-gray-100 text-gray-600' },
  suspended: { label: 'Suspended', color: 'bg-red-100 text-red-800' },
};

const OrganizationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [orgStats, setOrgStats] = useState<Record<string, OrganizationStats>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrganizationStatus | ''>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);

  // Check if user is provider_admin
  const isProviderAdmin = userProfile?.role === 'provider_admin';

  // Load organizations
  useEffect(() => {
    const loadOrganizations = async () => {
      if (!isProviderAdmin) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const orgs = await organizationService.getAll();
        setOrganizations(orgs);

        // Load stats for each org
        const statsMap: Record<string, OrganizationStats> = {};
        for (const org of orgs) {
          try {
            statsMap[org.id] = await organizationService.getStats(org.id);
          } catch (error) {
            console.error(`Error loading stats for org ${org.id}:`, error);
            statsMap[org.id] = { userCount: 0, projectCount: 0, activeWorkloadCount: 0, totalLeadCount: 0 };
          }
        }
        setOrgStats(statsMap);
      } catch (error) {
        console.error('Error loading organizations:', error);
        toast.error('Failed to load organizations');
      } finally {
        setIsLoading(false);
      }
    };

    loadOrganizations();
  }, [isProviderAdmin]);

  // Filter organizations
  const filteredOrganizations = organizations.filter((org) => {
    if (searchTerm && !org.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (statusFilter && org.status !== statusFilter) {
      return false;
    }
    return true;
  });

  // Handle status change
  const handleStatusChange = async (orgId: string, status: OrganizationStatus) => {
    try {
      await organizationService.updateStatus(orgId, status);
      setOrganizations((prev) =>
        prev.map((org) => (org.id === orgId ? { ...org, status } : org))
      );
      toast.success(`Organization ${status === 'active' ? 'activated' : status}`);
      setOpenMenuId(null);
      setMenuPosition(null);
    } catch (error) {
      console.error('Error updating organization status:', error);
      toast.error('Failed to update organization');
    }
  };

  // Handle organization created
  const handleOrganizationCreated = (newOrg: Organization) => {
    setOrganizations((prev) => [newOrg, ...prev]);
    setOrgStats((prev) => ({
      ...prev,
      [newOrg.id]: { userCount: 0, projectCount: 0, activeWorkloadCount: 0, totalLeadCount: 0 },
    }));
    setShowCreateModal(false);
  };

  // Access denied for non-provider_admin
  if (!isProviderAdmin) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-medium text-gray-900">Access Denied</h2>
          <p className="text-gray-500 mt-2">
            Only provider administrators can manage organizations.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Building2 className="h-7 w-7 mr-3 text-indigo-600" />
            Organizations
          </h1>
          <p className="text-gray-600 mt-1">
            Manage client organizations and their settings
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          <Plus className="h-5 w-5 mr-2" />
          Create Organization
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px] max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search organizations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as OrganizationStatus | '')}
            className="border border-gray-300 rounded-md px-4 py-2"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </div>

      {/* Organizations list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      ) : filteredOrganizations.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No organizations found</h3>
          <p className="text-gray-500 mt-2">
            {organizations.length === 0
              ? 'Create your first organization to get started.'
              : 'Try adjusting your filters.'}
          </p>
          {organizations.length === 0 && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create Organization
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Users
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Projects
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrganizations.map((org) => (
                <tr key={org.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <button
                      onClick={() => navigate(`/organizations/${org.id}`)}
                      className="text-left hover:text-indigo-600"
                    >
                      <p className="font-medium text-gray-900 hover:text-indigo-600">
                        {org.name}
                      </p>
                      {org.address && (
                        <p className="text-sm text-gray-500">
                          {org.address.city}, {org.address.state}
                        </p>
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        STATUS_CONFIG[org.status].color
                      }`}
                    >
                      {STATUS_CONFIG[org.status].label}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <Users className="h-4 w-4 mr-1" />
                      {orgStats[org.id]?.userCount || 0}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <FolderOpen className="h-4 w-4 mr-1" />
                      {orgStats[org.id]?.projectCount || 0}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {org.contactName && <p>{org.contactName}</p>}
                    {org.contactEmail && (
                      <p className="text-xs text-gray-400">{org.contactEmail}</p>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="relative inline-block text-left">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          if (openMenuId === org.id) {
                            setOpenMenuId(null);
                            setMenuPosition(null);
                          } else {
                            setMenuPosition({
                              top: rect.bottom + 4,
                              right: window.innerWidth - rect.right,
                            });
                            setOpenMenuId(org.id);
                          }
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded"
                      >
                        <MoreVertical className="h-5 w-5" />
                      </button>

                      {openMenuId === org.id && menuPosition && (
                        <>
                          <div
                            className="fixed inset-0 z-[100]"
                            onClick={() => {
                              setOpenMenuId(null);
                              setMenuPosition(null);
                            }}
                          />
                          <div
                            className="fixed w-48 bg-white rounded-md shadow-lg border border-gray-200 z-[101]"
                            style={{
                              top: menuPosition.top,
                              right: menuPosition.right,
                            }}
                          >
                            <button
                              onClick={() => navigate(`/organizations/${org.id}`)}
                              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </button>
                            <button
                              onClick={() => navigate(`/organizations/${org.id}?edit=true`)}
                              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </button>
                            <hr className="my-1" />
                            {org.status === 'active' ? (
                              <button
                                onClick={() => handleStatusChange(org.id, 'inactive')}
                                className="w-full flex items-center px-4 py-2 text-sm text-yellow-600 hover:bg-yellow-50"
                              >
                                <Power className="h-4 w-4 mr-2" />
                                Deactivate
                              </button>
                            ) : (
                              <button
                                onClick={() => handleStatusChange(org.id, 'active')}
                                className="w-full flex items-center px-4 py-2 text-sm text-green-600 hover:bg-green-50"
                              >
                                <Power className="h-4 w-4 mr-2" />
                                Activate
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateOrganizationModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleOrganizationCreated}
        />
      )}
    </div>
  );
};

export default OrganizationsPage;
